import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BeerFormState } from "../constants/types";
import { cacheConfig, QueryKeys } from "./constants";
import { togglePrebookProduct } from "../api/productApi";
import { productEndPoints } from "../constants/endpoints";
import { mapProducts } from "../utils/productListMapping";
import { fetchProductListingData } from "../api/ProductListAPI";
import { createProductService } from "../service/productService";
import { updateProductService } from "../service/productService";
import { updateDisableProduct } from "../api/EyeHideAPI";

export const useProducts = (enabled?: boolean) => {
  return useQuery({
    queryKey: [QueryKeys.Products],
    queryFn: () =>
      fetchProductListingData(productEndPoints.getProducts).then((data) =>
        mapProducts(data.products)
      ),
    enabled: enabled ?? true,
    ...cacheConfig.oneHour,
  });
};

const postProductMutation = async (
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string | undefined,
  displayInHome?: boolean,
  preorder?: boolean
) => {
  if (!productId) return;

  try {
    await togglePrebookProduct(productId, !!displayInHome);
  } catch (err) {
    console.warn("togglePrebookProduct failed:", err);
  }

  // Auto-hide product from customers when pre-booking is enabled
  if (preorder) {
    try {
      await updateDisableProduct(productEndPoints.editProduct, productId, true);
    } catch (err) {
      console.warn("Failed to auto-hide pre-booked product:", err);
    }
  }

  await new Promise((resolve) => setTimeout(resolve, 1000));

  queryClient.invalidateQueries({ queryKey: [QueryKeys.Products] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.BrandConfig] });
};

export const useUpdateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, id }: any) => updateProductService(data, id),
    onSuccess: async (_, variables) => {
      await postProductMutation(
        queryClient,
        variables.id,
        variables.data?.preorder,
        variables.data?.preorder
      );
    },
    onError: (error: any) => {
      console.error("Failed to update product", error);
    },
  });
};

export const useCreateProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, BeerFormState>({
    mutationFn: (data) => createProductService(data),
    onSuccess: async (response, variables) => {
      const productId =
        response?.product?.id ??
        response?.data?.product?.id ??
        response?.data?.product?.data?.id;

      await postProductMutation(
        queryClient,
        productId,
        variables?.preorder,
        variables?.preorder
      );
    },
    onError: (error) => {
      console.error("Error creating product:", error);
    },
  });
};
